import React, { useEffect, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import { Link, useNavigate } from "react-router-dom";
import PaginationComponent from "../../../Components/PaginationComponent";
import { BsThreeDotsVertical } from "react-icons/bs";
import EquipmentDetailModal from "../../../Components/EquipmentDetailModal";
import equipmentService from "../../../Services/inventoryService";
import toast from "react-hot-toast";
import { IoMdAdd } from "react-icons/io";


const Equipments = () => {
  const navigate = useNavigate();

  const [searchData, setSearchData]       = useState("");
  const [filteredData, setFilteredData]   = useState([]);
  const [paginatedData, setPaginatedData] = useState([]);
  const itemsPerPage                      = 10;
  const [currentPage, setCurrentPage]     = useState(1);
  const [loading, setLoading]             = useState(false);
  const [detailItem, setDetailItem]       = useState(null);
  const [records, setRecords]             = useState([]);

  useEffect(() => {
    const fetchRecords = async () => {
      setLoading(true);
      try {
        const data = await equipmentService.fetchAll();
        setRecords(data.inventories);
      } catch (error) {
        toast.error("Failed to fetch equipment records.");
      } finally {
        setLoading(false);
      }
    };
    fetchRecords();
  }, []);

  useEffect(() => {
    const filtered =
      records?.filter((item) =>
        item?.name_of_equipment?.toLowerCase().includes(searchData.toLowerCase())
      ) || [];
    setFilteredData(filtered);
    setCurrentPage(1);
  }, [searchData, records]);

  useEffect(() => {
    const start = (currentPage - 1) * itemsPerPage;
    const end   = start + itemsPerPage;
    setPaginatedData(filteredData.slice(start, end));
  }, [currentPage, filteredData]);

  const showCostOfMaintenance  = filteredData.some((item) => item.cost_of_maintenance);
  const showDateOfCondemnation = filteredData.some((item) => item.date_of_condemnation);
  const showDateOfReplacement  = filteredData.some((item) => item.date_of_replacement);
  const showOtherRemarks       = filteredData.some((item) => item.other_relevant_remarks);

  const columns = [
    {
      key: "sr",
      label: "Sr No.",
      render: (item, index) => (currentPage - 1) * itemsPerPage + index + 1,
    },
    {
      key: "name_of_equipment",
      label: "Name of Equipment",
      render: (item) => (
        <span className="font-[500] text-gray-800 whitespace-nowrap">
          {item.name_of_equipment}
        </span>
      ),
    },
    {
      key: "date_of_purchase",
      label: "Date of Purchase",
      render: (item) =>
        item.date_of_purchase
          ? new Date(item.date_of_purchase).toLocaleDateString()
          : <span className="text-gray-300">—</span>,
    },
    {
      key: "cost_of_equipment",
      label: "Cost of Equipment",
      render: (item) =>
        item.cost_of_equipment
          ? `Rs. ${item.cost_of_equipment}`
          : <span className="text-gray-300">—</span>,
    },
    {
      key: "nameAndAddressOfSupplier",
      label: "Name & Address of Supplier",
      render: (item) => (
        <span className="max-w-[180px] block truncate" title={item.nameAndAddressOfSupplier}>
          {item.nameAndAddressOfSupplier || <span className="text-gray-300">—</span>}
        </span>
      ),
    },
    {
      key: "date_of_manufacture",
      label: "Date of Manufacture",
      render: (item) =>
        item.date_of_manufacture
          ? new Date(item.date_of_manufacture).toLocaleDateString()
          : <span className="text-gray-300">—</span>,
    },
    {
      key: "date_of_installation",
      label: "Date of Installation",
      render: (item) =>
        item.date_of_installation
          ? new Date(item.date_of_installation).toLocaleDateString()
          : <span className="text-gray-300">—</span>,
    },
    {
      key: "department_where_intalled",
      label: "Department Where Installed",
      render: (item) => item.department_where_intalled || <span className="text-gray-300">—</span>,
    },
    {
      key: "environmental_control",
      label: "Environmental Control",
      render: (item) => item.environmental_control || <span className="text-gray-300">—</span>,
    },
    {
      key: "spare_parts_inventory",
      label: "Spare Parts Inventory",
      render: (item) => item.spare_parts_inventory || <span className="text-gray-300">—</span>,
    },
    {
      key: "technical_manualCircuit_diagramLiterature",
      label: "Technical Manual / Circuit Diagrams",
      render: (item) => item.technical_manualCircuit_diagramLiterature || <span className="text-gray-300">—</span>,
    },
    {
      key: "after_sale_service_arrangement",
      label: "After Sale Service Arrangement",
      render: (item) => item.after_sale_service_arrangement || <span className="text-gray-300">—</span>,
    },
    {
      key: "warranty_period",
      label: "Warranty Period",
      render: (item) => item.warranty_period || <span className="text-gray-300">—</span>,
    },
    {
      key: "life_of_equipment",
      label: "Life of Equipment",
      render: (item) => item.life_of_equipment || <span className="text-gray-300">—</span>,
    },
    {
      key: "depreciation_per_year",
      label: "Depreciation Per Year",
      render: (item) =>
        item.depreciation_per_year
          ? `Rs. ${item.depreciation_per_year}`
          : <span className="text-gray-300">—</span>,
    },
    {
      key: "cost_of_maintenance",
      label: "Cost of Maintenance",
      hidden: !showCostOfMaintenance,
      render: (item) =>
        item.cost_of_maintenance
          ? `Rs. ${item.cost_of_maintenance}`
          : <span className="text-gray-300">—</span>,
    },
    {
      key: "date_of_condemnation",
      label: "Date of Condemnation",
      hidden: !showDateOfCondemnation,
      render: (item) =>
        item.date_of_condemnation
          ? new Date(item.date_of_condemnation).toLocaleDateString()
          : <span className="text-gray-300">—</span>,
    },
    {
      key: "date_of_replacement",
      label: "Date of Replacement",
      hidden: !showDateOfReplacement,
      render: (item) =>
        item.date_of_replacement
          ? new Date(item.date_of_replacement).toLocaleDateString()
          : <span className="text-gray-300">—</span>,
    },
    {
      key: "other_relevant_remarks",
      label: "Other Relevant Remarks",
      hidden: !showOtherRemarks,
      render: (item) => (
        <span className="max-w-[200px] block truncate" title={item.other_relevant_remarks}>
          {item.other_relevant_remarks || <span className="text-gray-300">—</span>}
        </span>
      ),
    },
    {
      key: "update",
      label: "Update",
      render: (item) => (
        <button
          onClick={() =>
            navigate(`/account/equipment/update/${item?.id}`, {
              state: { equipment: item },
            })
          }
          className="text-white text-[11px] px-3 py-1.5 rounded-md bg-[#2F6DB4] hover:bg-[#265d9e] transition-colors whitespace-nowrap"
        >
          Update
        </button>
      ),
    },
    {
      key: "details",
      label: "Details",
      render: (item) => (
        <button
          onClick={() => setDetailItem(item)}
          className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition-colors"
          title="View details"
        >
          <BsThreeDotsVertical size={16} />
        </button>
      ),
    },
  ];

  const visibleColumns = columns.filter((col) => !col.hidden);

  const MobileCard = ({ item, index }) => {
    const srNo = (currentPage - 1) * itemsPerPage + index + 1;
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <span className="shrink-0 w-6 h-6 rounded-full bg-[#2F6DB4] text-white text-[10px] font-semibold flex items-center justify-center">
              {srNo}
            </span>
            <p className="text-[14px] font-semibold text-gray-800 truncate">
              {item.name_of_equipment || "—"}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() =>
                navigate(`/account/equipment/update/${item?.id}`, {
                  state: { equipment: item },
                })
              }
              className="text-white text-[11px] px-3 py-1.5 rounded-md bg-[#2F6DB4] hover:bg-[#265d9e] transition-colors whitespace-nowrap"
            >
              Update
            </button>
            <button
              onClick={() => setDetailItem(item)}
              className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition-colors border border-gray-200"
              title="View details"
            >
              <BsThreeDotsVertical size={16} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
          {item.date_of_purchase && (
            <div>
              <p className="text-[10px] uppercase tracking-wide text-gray-400 font-medium">
                Purchase Date
              </p>
              <p className="text-[12px] text-gray-700">
                {new Date(item.date_of_purchase).toLocaleDateString()}
              </p>
            </div>
          )}
          {item.cost_of_equipment && (
            <div>
              <p className="text-[10px] uppercase tracking-wide text-gray-400 font-medium">
                Cost
              </p>
              <p className="text-[12px] text-gray-700">Rs. {item.cost_of_equipment}</p>
            </div>
          )}
          {item.department_where_intalled && (
            <div>
              <p className="text-[10px] uppercase tracking-wide text-gray-400 font-medium">
                Department
              </p>
              <p className="text-[12px] text-gray-700 truncate">
                {item.department_where_intalled}
              </p>
            </div>
          )}
          {item.warranty_period && (
            <div>
              <p className="text-[10px] uppercase tracking-wide text-gray-400 font-medium">
                Warranty
              </p>
              <p className="text-[12px] text-gray-700">{item.warranty_period}</p>
            </div>
          )}
          {item.life_of_equipment && (
            <div>
              <p className="text-[10px] uppercase tracking-wide text-gray-400 font-medium">
                Life
              </p>
              <p className="text-[12px] text-gray-700">{item.life_of_equipment}</p>
            </div>
          )}
          {item.date_of_installation && (
            <div>
              <p className="text-[10px] uppercase tracking-wide text-gray-400 font-medium">
                Installed
              </p>
              <p className="text-[12px] text-gray-700">
                {new Date(item.date_of_installation).toLocaleDateString()}
              </p>
            </div>
          )}
        </div>

        <p className="mt-3 text-[10px] text-gray-300 text-right">
          Tap ⋮ for full details
        </p>
      </div>
    );
  };

  return (
   <div className="p-3 sm:p-6 w-full min-w-0 max-w-[1100px] xl:max-w-[1600px] mx-auto">

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white border border-gray-200 rounded-xl px-4 sm:px-6 py-4 mb-5 shadow-sm">
        <div>
          <h1 className="text-[16px] sm:text-[17px] font-[500] text-gray-800">
            Equipment Maintenance Records
          </h1>
          <p className="text-[12px] text-gray-400 mt-0.5">
            {filteredData.length} records found
          </p>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <div className="relative flex-1 sm:flex-none">
            <SearchIcon
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
              style={{ fontSize: 16 }}
            />
            <input
              type="search"
              placeholder="Search equipment..."
              value={searchData}
              onChange={(e) => setSearchData(e.target.value)}
              className="pl-8 pr-3 py-[7px] w-full sm:w-[240px] text-[13px] bg-gray-50 border border-gray-200 rounded-lg focus:outline-none"
            />
          </div>

          <Link to="add" className="shrink-0">
            <button className="relative overflow-hidden bg-[#2F6DB4] text-white text-[12px] sm:text-[13px] font-[500] px-3 sm:px-4 py-2 rounded-lg border border-gray-300 shadow-md hover:shadow-gray-200 hover:scale-[1.03] active:scale-[0.97] transition-all duration-300 before:absolute before:inset-0 before:-translate-x-full hover:before:translate-x-full before:bg-white/20 before:transition-transform before:duration-500 whitespace-nowrap">
              <IoMdAdd className="inline mb-0.5" size={16} /> Add Equipments
            </button>
          </Link>
        </div>
      </div>

      <div className="hidden sm:block bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden w-full">
        <div className="overflow-x-auto overflow-y-auto max-h-[60vh]">
          <table className="w-max min-w-full border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="bg-[#2F6DB4] text-[11px] uppercase text-white">
                {visibleColumns.map((col) => (
                  <th
                    key={col.key}
                    className="py-3 px-4 text-left whitespace-nowrap border-gray-700"
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={visibleColumns.length} className="py-10 text-center text-[13px] text-gray-400">
                    Loading equipment records...
                  </td>
                </tr>
              ) : paginatedData.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumns.length} className="py-10 text-center text-[13px] text-gray-400">
                    No equipment records found.
                  </td>
                </tr>
              ) : (
                paginatedData.map((item, index) => (
                  <tr
                    key={item?.id}
                    className="border-t border-gray-100 hover:bg-gray-50 text-[13px] text-gray-700"
                  >
                    {visibleColumns.map((col) => (
                      <td
                        key={col.key}
                        className={`py-3 px-4 border-gray-100 last:border-r-0 ${
                          col.key === "update" || col.key === "details" ? "text-center" : ""
                        }`}
                      >
                        {col.render(item, index)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:hidden">
        {loading ? (
          <div className="bg-white border border-gray-200 rounded-xl py-10 text-center text-[13px] text-gray-400 shadow-sm">
            Loading equipment records...
          </div>
        ) : paginatedData.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl py-10 text-center text-[13px] text-gray-400 shadow-sm">
            No equipment records found.
          </div>
        ) : (
          paginatedData.map((item, index) => (
            <MobileCard key={item?.id} item={item} index={index} />
          ))
        )}
      </div>

      <PaginationComponent
        filteredData={filteredData}
        setPaginatedData={setPaginatedData}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
      />

      {detailItem && (
        <EquipmentDetailModal
          item={detailItem}
          onClose={() => setDetailItem(null)}
        />
      )}
    </div>
  );
};

export default Equipments;